import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { initializeTheme } from "@/lib/themes";
import ProtectedRoute from "@/components/ProtectedRoute";
import AgeVerification from "@/components/AgeVerification";
import GDPRBanner from "@/components/GDPRBanner";
import Login from "@/pages/Login";
import PlatformDataDashboard from "@/pages/PlatformDataDashboard";
import ProjectHealth from "@/pages/ProjectHealthDashboard";
import PostsUpdatesManager from "@/pages/PostsUpdatesManager";
import CreatorCommandCenter from "@/pages/CreatorCommandCenter";
import DMReplies from "@/pages/DMReplies";
import TikTokStrategy from "@/pages/TikTokStrategy";
import Calendar from "@/pages/Calendar";
import Sequences from "@/pages/Sequences";
import Library from "@/pages/Library";
import Collabs from "@/pages/Collabs";
import InboxParser from "@/pages/Inbox";
import Sexting from "@/pages/Sexting";
import TickTick from "@/pages/TickTick";
import Stats from "@/pages/Stats";
import Videos from "@/pages/Videos";
import FreeMembers from "@/pages/FreeMembers";
import FreeVideoPreview from "@/pages/FreeVideoPreview";
import CastProfile from "@/pages/CastProfile";
import VideoGalleryManager from "@/pages/VideoGalleryManager";
import StudioPage from "@/pages/StudioPage";
import CollabForm from "@/pages/CollabForm";
import Submissions from "@/pages/Submissions";
import CRM from "@/pages/CRM";
import FanCRM from "@/pages/crm/FanCRM";
import Pipeline from "@/pages/Pipeline";
import ModelApply from "@/pages/ModelApply";
import ModelApplyNew from "@/pages/ModelApplyNew";
import UnifiedApplyPage from "@/pages/UnifiedApplyPage";
import CompanionBooking from "@/pages/AvailabilityEnquiry";
import CompanionBookingsAdmin from "@/pages/CompanionBookingsAdmin";
import AdminMembers from "@/pages/AdminMembers";
import MemberAreasConfig from "@/pages/MemberAreasConfig";
import EventsShows from "@/pages/EventsShows";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import Catalog from "@/pages/Catalog";
import Pictures from "@/pages/Pictures";
import StudioScenes from "@/pages/StudioScenes";
import XStudio from "@/pages/XStudio";
import IntimalyHub from "@/pages/IntimalyHub";
import OnlyFansFreeHub from "@/pages/OnlyFansFreeHub";
import OnlyFansVIPHub from "@/pages/OnlyFansVIPHub";
import RFCHub from "@/pages/RFCHub";
import TheBlowersHub from "@/pages/TheBlowersHub";
import Studios from "@/pages/Studios";  // legacy — still routed for deep links
import StudiosPage from "@/pages/StudiosPage";  // new Studios page
import ExtensionFans from "@/pages/ExtensionFans";
import Faphouse from "@/pages/Faphouse";
import FaphouseHub from "@/pages/FaphouseHub";
import Consents from "@/pages/Consents";
import JustForFans from "@/pages/JustForFans";
import Legal from "@/pages/Legal";
import Backups from "@/pages/Backups";
import NotFound from "@/pages/NotFound";
import EmailSection from "@/pages/email/EmailSection";
import SocialSection from "@/pages/social/SocialSection";
import AIStudioSection from "@/pages/ai/AIStudioSection";
import GrokAI from "@/pages/GrokAI";
import ExtensionPostQueue from "@/pages/ExtensionPostQueue";
import ExtensionCookies from "@/pages/ExtensionCookies";
import MoneySection from "@/pages/money/MoneySection";
import SectionRedirect from "@/components/sections/SectionRedirect";
import { SECTION_REDIRECTS } from "@/components/sections/sectionTabs";
import Deals from "@/pages/Deals";
import Tours from "@/pages/Tours";
import EventVenueDiscovery from "@/pages/crm/EventVenueDiscovery";
import StudioDiscovery from "@/pages/crm/StudioDiscovery";
import ComplianceDashboard from "@/pages/crm/ComplianceDashboard";
import ContentProtection from "@/pages/ContentProtection";
import PricingStrategy from "@/pages/PricingStrategy";
import ContentProtectionDetail from "@/pages/ContentProtectionDetail";
import PlatformOverview from "@/pages/crm/PlatformOverview";
import PlatformHub from "@/pages/PlatformHub";
import SearchHub from "@/pages/crm/SearchHub";
import ContentROICalculator from "@/pages/ContentROICalculator";
import PublicLanding from "@/pages/PublicLanding";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import UserDeletion from "@/pages/UserDeletion";
import DeletionStatus from "@/pages/DeletionStatus";
import LegalHub from "@/pages/LegalHub";
import Takedown from "@/pages/Takedown";
import AboutPage from "@/pages/AboutPage";
import FAQPage from "@/pages/FAQPage";
import UpdatesPage from "@/pages/UpdatesPage";
import PageEditor from "@/pages/PageEditor";
import LandingEditorNew from "@/pages/LandingEditorNew";
import Settings from "@/pages/Settings";
import { PublicMessagesPage, EscortBookingsPage } from "@/pages/PublicIntake";
import StorePage from "@/pages/StorePage";
import StoreVideoDetailPage from "@/pages/StoreVideoDetailPage";
import StoreSuccessPage from "@/pages/StoreSuccessPage";
import StoreWatchPage from "@/pages/StoreWatchPage";
import StoreManagement from "@/pages/StoreManagement";
import StoreManagementEnhanced from "@/pages/StoreManagementEnhanced";
import MyLocker from "@/pages/MyLocker";
import AdminOTPLogin from "@/pages/AdminOTPLogin";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import MemberLogin from "@/pages/MemberLogin";
import MemberRegister from "@/pages/MemberRegister";
import MemberForgotPassword from "@/pages/MemberForgotPassword";
import MembersArea from "@/pages/MembersArea";
import FreeMembersArea from "@/pages/FreeMembersArea";
import MembersAreaWatch from "@/pages/MembersAreaWatch";
import MemberUpgrade from "@/pages/MemberUpgrade";
import MemberProtectedRoute from "@/components/MemberProtectedRoute";
import MaintenanceMode from "@/pages/MaintenanceMode";
import SubscriptionDashboard from "@/pages/crm/SubscriptionDashboard";
import AdsManagement from "@/pages/crm/AdsManagement";
import ContentDashboard from "@/pages/crm/ContentDashboard";
import MasterDashboard from "@/pages/crm/MasterDashboard";
import CRMCleanup from "@/pages/crm/CRMCleanup";
import ProjectStatusReport from "@/pages/ProjectStatusReport";
import LeadDiscovery from "@/pages/crm/LeadDiscovery";
import VenueDiscovery from "@/pages/crm/VenueDiscovery";
import DirectoryTracker from "@/pages/crm/DirectoryTracker";
import AdminMessages from "@/pages/crm/AdminMessages";
import LegalDocsEditor from "@/pages/crm/LegalDocsEditor";
import { VoiceProvider} from "@/context/VoiceContext";
import OfflineIndicator from "@/components/OfflineIndicator";
import InstallPrompt from "@/components/InstallPrompt";
import CoPilot from "@/components/CoPilot";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";

// Public Video Store is hidden; /store routes redirect to home while false.
const PUBLIC_STORE_ENABLED = false;

export default function App() {
  // Initialize theme on app load
  React.useEffect(() => {
    initializeTheme();
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <VoiceProvider>
        <GDPRBanner />
        <OfflineIndicator />
        <InstallPrompt />
        <BrowserRouter>
          {/* CoPilot widget hidden globally per user request */}
          {/* <CoPilot /> */}
          <RouteErrorBoundary>
          <Routes>
            {/* PUBLIC routes (no auth) */}
            <Route path="/" element={<PublicLanding />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/updates" element={<UpdatesPage />} />
            <Route path="/legal" element={<LegalHub />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/takedown" element={<Takedown />} />
            <Route path="/user-deletion" element={<UserDeletion />} />
            <Route path="/deletion/status" element={<DeletionStatus />} />
            <Route path="/deletion" element={<UserDeletion />} />
            <Route path="/login" element={<Login />} />
            <Route path="/gpcrmcontrol" element={<AdminOTPLogin />} />
            <Route path="/subscription-success" element={<SubscriptionSuccess />} />
            <Route path="/members/login" element={<MemberLogin />} />
            <Route path="/members/register" element={<MemberRegister />} />
            <Route path="/members/forgot-password" element={<MemberForgotPassword />} />
            <Route path="/subscribe" element={<Navigate to="/members/upgrade" replace />} />
            {/* Member Areas - Tiered Access */}
            <Route path="/members/free" element={<MemberProtectedRoute><FreeMembersArea /></MemberProtectedRoute>} />
            <Route path="/members/upgrade" element={<MemberProtectedRoute><MemberUpgrade /></MemberProtectedRoute>} />
            <Route path="/members/basic" element={<MembersArea />} />
            <Route path="/members-area" element={<MembersArea />} />
            <Route path="/members-area/watch/:videoId" element={<MembersAreaWatch />} />
            <Route path="/form"  element={<CollabForm />} />
            <Route path="/apply" element={<UnifiedApplyPage />} />
            <Route path="/collabapply" element={<ModelApplyNew />} />
            <Route path="/modelapply" element={<ModelApplyNew />} />
            <Route path="/modelapply-old" element={<ModelApply />} />
            <Route path="/escort-booking" element={<CompanionBooking />} />
            <Route path="/companion-booking" element={<CompanionBooking />} />
            {/* Video Store (Public) */}
            <Route path="/store" element={PUBLIC_STORE_ENABLED ? <StorePage /> : <Navigate to="/" replace />} />
            <Route path="/store/:videoId" element={PUBLIC_STORE_ENABLED ? <StoreVideoDetailPage /> : <Navigate to="/" replace />} />
            <Route path="/store/watch/:videoId" element={PUBLIC_STORE_ENABLED ? <StoreWatchPage /> : <Navigate to="/" replace />} />
            <Route path="/store/success" element={PUBLIC_STORE_ENABLED ? <StoreSuccessPage /> : <Navigate to="/" replace />} />
            <Route path="/my-locker" element={<MyLocker />} />
            
            {/* Free Video Preview - no auth required */}
            <Route path="/video/:videoId/preview" element={<FreeVideoPreview />} />
            <Route path="/cast/:slug" element={<CastProfile />} />
            
            {/* Admin Gallery Manager */}
            <Route path="/admin/videos/:videoId/gallery" element={<ProtectedRoute><VideoGalleryManager /></ProtectedRoute>} />
            
            {/* Studio Pages - public */}
            <Route path="/studios/:slug" element={<StudioPage />} />

            {/* APP routes — now namespaced under /crm/* (e.g. giopilos.com/crm/finance) */}
            {/* Command Center - Unified Dashboard (Default Home) */}
            <Route path="/crm" element={<Navigate to="/crm/command-center" replace />} />
            <Route path="/crm/command-center" element={<ProtectedRoute><CreatorCommandCenter /></ProtectedRoute>} />
            <Route path="/crm/project-health" element={<ProtectedRoute><ProjectHealth /></ProtectedRoute>} />
            <Route path="/crm/posts-updates" element={<ProtectedRoute><PostsUpdatesManager /></ProtectedRoute>} />
            <Route path="/crm/live-sync" element={<ProtectedRoute><PlatformDataDashboard /></ProtectedRoute>} />
            <Route path="/crm/members" element={<ProtectedRoute layout="Members"><AdminMembers /></ProtectedRoute>} />
            <Route path="/crm/member-areas" element={<ProtectedRoute layout="Member Areas"><MemberAreasConfig /></ProtectedRoute>} />
            <Route path="/crm/studio-scenes" element={<ProtectedRoute><StudioScenes /></ProtectedRoute>} />
            {/* Redirects for old dashboard routes */}
            <Route path="/crm/dashboard" element={<Navigate to="/crm/command-center" replace />} />
            <Route path="/dashboard" element={<Navigate to="/crm/command-center" replace />} />
            <Route path="/crm/admin" element={<Navigate to="/crm/command-center" replace />} />
            <Route path="/crm/master" element={<ProtectedRoute><CRM key="master" scope="all" title="Master CRM" overline="ALL RECORDS · SHARED CRM SOURCE" /></ProtectedRoute>} />
            <Route path="/crm/crew" element={<ProtectedRoute><CRM key="crew" scope="crew" title="Team Directory" overline="CRM SUBVIEW · MODELS + ESCORTS + CREW + PRODUCTION ROLES + STUDIOS" scopeTypes={["Model", "Performer", "Escort", "Crew", "Producer", "Cameraman", "Team", "Studio"]} /></ProtectedRoute>} />
            <Route path="/crm/brands" element={<ProtectedRoute><CRM key="brands" scope="brands" title="Brands" overline="CRM SUBVIEW · BRAND PARTNERS" scopeTypes={["Brand"]} /></ProtectedRoute>} />
            <Route path="/crm/brands-events" element={<Navigate to="/crm/events-shows" replace />} />
            <Route path="/crm/cleanup" element={<ProtectedRoute><CRMCleanup /></ProtectedRoute>} />
            <Route path="/crm/compliance" element={<ProtectedRoute layout="Compliance"><ComplianceDashboard /></ProtectedRoute>} />
            {/* Merged admin sections (phase 2). Old page URLs redirect to their section tab. */}
            <Route path="/crm/money" element={<ProtectedRoute><MoneySection /></ProtectedRoute>} />
            <Route path="/crm/ai" element={<ProtectedRoute><AIStudioSection /></ProtectedRoute>} />
            <Route path="/crm/grok-ai" element={<ProtectedRoute><GrokAI /></ProtectedRoute>} />
            <Route path="/crm/extension-post-queue" element={<ProtectedRoute><ExtensionPostQueue /></ProtectedRoute>} />
            <Route path="/crm/extension-cookies" element={<ProtectedRoute><ExtensionCookies /></ProtectedRoute>} />
            <Route path="/crm/email" element={<ProtectedRoute><EmailSection /></ProtectedRoute>} />
            {Object.entries(SECTION_REDIRECTS).map(([from, to]) => (
              <Route key={from} path={from} element={<SectionRedirect to={to} />} />
            ))}
            <Route path="/crm/pricing-strategy" element={<ProtectedRoute><PricingStrategy /></ProtectedRoute>} />
            <Route path="/crm/content-protection" element={<ProtectedRoute><ContentProtection /></ProtectedRoute>} />
            <Route path="/crm/content-protection/:id" element={<ProtectedRoute><ContentProtectionDetail /></ProtectedRoute>} />
            <Route path="/crm/platform-overview" element={<Navigate to="/crm/platform-hub" replace />} />
            <Route path="/crm/platform-hub" element={<ProtectedRoute><PlatformHub /></ProtectedRoute>} />
            <Route path="/crm/search-hub" element={<ProtectedRoute layout="Search Hub"><SearchHub /></ProtectedRoute>} />
            
            <Route path="/crm/contacts"     element={<ProtectedRoute><CRM key="models" scope="models" title="Models" overline="CRM SUBVIEW · MODELS & TALENT" scopeTypes={["Model", "Performer"]} /></ProtectedRoute>} />
            <Route path="/crm/escorts"      element={<ProtectedRoute><CRM key="escorts" scope="escorts" title="Escorts" overline="CRM SUBVIEW · COMPANION SERVICES" scopeTypes={["Escort"]} /></ProtectedRoute>} />
            <Route path="/crm/fans"         element={<ProtectedRoute><FanCRM /></ProtectedRoute>} />
            <Route path="/crm/extension-fans" element={<ProtectedRoute><ExtensionFans /></ProtectedRoute>} />
            <Route path="/crm/fan-crm"      element={<ProtectedRoute><FanCRM /></ProtectedRoute>} />
            <Route path="/crm/studios"      element={<ProtectedRoute><CRM key="studios" scope="studios" title="Studios" overline="CRM SUBVIEW · STUDIO ENTITIES" scopeTypes={["Studio"]} /></ProtectedRoute>} />
            <Route path="/crm/companion-bookings" element={<ProtectedRoute><CompanionBookingsAdmin /></ProtectedRoute>} />
            <Route path="/crm/dm-replies"   element={<ProtectedRoute><DMReplies /></ProtectedRoute>} />
            <Route path="/crm/tiktok"       element={<ProtectedRoute><TikTokStrategy /></ProtectedRoute>} />
            <Route path="/crm/calendar"     element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
            <Route path="/crm/sequences"    element={<ProtectedRoute><Sequences /></ProtectedRoute>} />
            <Route path="/crm/library"      element={<ProtectedRoute><Library /></ProtectedRoute>} />
            <Route path="/crm/collabs"      element={<ProtectedRoute><Collabs /></ProtectedRoute>} />
            <Route path="/crm/deals"        element={<ProtectedRoute><Deals /></ProtectedRoute>} />
            <Route path="/crm/tours"        element={<ProtectedRoute><Tours /></ProtectedRoute>} />
            <Route path="/crm/pipeline"     element={<ProtectedRoute><Pipeline /></ProtectedRoute>} />
            <Route path="/crm/inbox"        element={<ProtectedRoute><InboxParser /></ProtectedRoute>} />
            <Route path="/crm/sexting"      element={<ProtectedRoute><Sexting /></ProtectedRoute>} />
            <Route path="/crm/ticktick"     element={<ProtectedRoute><TickTick /></ProtectedRoute>} />
            <Route path="/crm/stats"        element={<ProtectedRoute><Stats /></ProtectedRoute>} />
            <Route path="/crm/videos"       element={<ProtectedRoute><Videos /></ProtectedRoute>} />
            <Route path="/crm/catalog"      element={<ProtectedRoute><Catalog /></ProtectedRoute>} />
            <Route path="/crm/content-library" element={<Navigate to="/crm/catalog" replace />} />
            {/* Store Management - Dedicated Enhanced Page */}
            <Route path="/crm/store-management" element={<ProtectedRoute layout="Store Management"><StoreManagementEnhanced /></ProtectedRoute>} />
            <Route path="/crm/subscriptions" element={<ProtectedRoute><SubscriptionDashboard /></ProtectedRoute>} />
            <Route path="/crm/ads-management" element={<ProtectedRoute><AdsManagement /></ProtectedRoute>} />
            <Route path="/crm/legal-editor" element={<ProtectedRoute><LegalDocsEditor /></ProtectedRoute>} />
            <Route path="/crm/legal-docs-editor" element={<ProtectedRoute><LegalDocsEditor /></ProtectedRoute>} />
            <Route path="/crm/content" element={<ProtectedRoute><ContentDashboard /></ProtectedRoute>} />
            <Route path="/crm/project-status" element={<ProtectedRoute layout="Project Status"><ProjectStatusReport /></ProtectedRoute>} />
            <Route path="/crm/lead-discovery" element={<ProtectedRoute layout="Lead Discovery"><LeadDiscovery /></ProtectedRoute>} />
            <Route path="/crm/venue-discovery" element={<ProtectedRoute layout="Venue Discovery"><VenueDiscovery /></ProtectedRoute>} />
            <Route path="/crm/directory-tracker" element={<ProtectedRoute><DirectoryTracker /></ProtectedRoute>} />
            <Route path="/crm/messages" element={<ProtectedRoute><AdminMessages /></ProtectedRoute>} />
            <Route path="/crm/x-analytics"  element={<Navigate to="/crm/x?tab=analytics" replace />} />
            <Route path="/crm/jff"          element={<ProtectedRoute><JustForFans /></ProtectedRoute>} />
            <Route path="/crm/intimaly-hub" element={<ProtectedRoute><IntimalyHub /></ProtectedRoute>} />
            <Route path="/crm/x-hub"        element={<ProtectedRoute><XStudio /></ProtectedRoute>} />
            <Route path="/crm/platform-hubs/x" element={<ProtectedRoute><XStudio /></ProtectedRoute>} />
            <Route path="/crm/onlyfans-free" element={<ProtectedRoute><OnlyFansFreeHub /></ProtectedRoute>} />
            <Route path="/crm/onlyfans-vip" element={<ProtectedRoute><OnlyFansVIPHub /></ProtectedRoute>} />
            <Route path="/crm/rfc" element={<ProtectedRoute><RFCHub /></ProtectedRoute>} />
            <Route path="/crm/intimaly" element={<ProtectedRoute><IntimalyHub /></ProtectedRoute>} />
            <Route path="/crm/theblowers" element={<ProtectedRoute><TheBlowersHub /></ProtectedRoute>} />
            <Route path="/crm/x"            element={<ProtectedRoute><XStudio /></ProtectedRoute>} />
            <Route path="/crm/events-shows" element={<ProtectedRoute><EventsShows /></ProtectedRoute>} />
            <Route path="/crm/shows" element={<Navigate to="/crm/events-shows" replace />} />
            <Route path="/crm/analytics"    element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
            <Route path="/crm/submissions"  element={<ProtectedRoute><Submissions /></ProtectedRoute>} />
            <Route path="/crm/social"       element={<ProtectedRoute><SocialSection /></ProtectedRoute>} />
            <Route path="/crm/faphouse"     element={<ProtectedRoute><Faphouse /></ProtectedRoute>} />
            <Route path="/crm/legal"        element={<ProtectedRoute><Legal /></ProtectedRoute>} />
            <Route path="/crm/backups"      element={<ProtectedRoute><Backups /></ProtectedRoute>} />
            <Route path="/crm/landing"      element={<ProtectedRoute><LandingEditorNew /></ProtectedRoute>} />
            <Route path="/crm/pages"        element={<ProtectedRoute><PageEditor /></ProtectedRoute>} />
            <Route path="/crm/settings"     element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/crm/x/inbox"      element={<Navigate to="/crm/x?tab=inbox" replace />} />
            <Route path="/crm/inbox/messages" element={<ProtectedRoute><PublicMessagesPage /></ProtectedRoute>} />
            <Route path="/crm/escort-bookings" element={<ProtectedRoute><EscortBookingsPage /></ProtectedRoute>} />
            <Route path="/x/inbox"          element={<Navigate to="/crm/x?tab=inbox" replace />} />

            {/* LEGACY flat aliases — kept so all existing in-app <Link to="/finance"> keep working */}
            <Route path="/dm-replies" element={<ProtectedRoute><DMReplies /></ProtectedRoute>} />
            <Route path="/tiktok"     element={<ProtectedRoute><TikTokStrategy /></ProtectedRoute>} />
            <Route path="/calendar"   element={<Navigate to="/crm/calendar" replace />} />
            <Route path="/sequences"  element={<ProtectedRoute><Sequences /></ProtectedRoute>} />
            <Route path="/library"    element={<ProtectedRoute><Library /></ProtectedRoute>} />
            <Route path="/collabs"    element={<ProtectedRoute><Collabs /></ProtectedRoute>} />
            <Route path="/deals"      element={<ProtectedRoute><Deals /></ProtectedRoute>} />
            <Route path="/tours"      element={<ProtectedRoute><Tours /></ProtectedRoute>} />
            <Route path="/pipeline"   element={<ProtectedRoute><Pipeline /></ProtectedRoute>} />
            <Route path="/inbox"      element={<ProtectedRoute><InboxParser /></ProtectedRoute>} />
            <Route path="/sexting"    element={<ProtectedRoute><Sexting /></ProtectedRoute>} />
            <Route path="/ticktick"   element={<ProtectedRoute><TickTick /></ProtectedRoute>} />
            <Route path="/stats"      element={<ProtectedRoute><Stats /></ProtectedRoute>} />
            <Route path="/videos"     element={<ProtectedRoute><Videos /></ProtectedRoute>} />
            <Route path="/free-members" element={<FreeMembers />} />
            <Route path="/catalog"    element={<ProtectedRoute><Catalog /></ProtectedRoute>} />
            <Route path="/store-management" element={<ProtectedRoute layout="Store Management"><StoreManagementEnhanced /></ProtectedRoute>} />
            <Route path="/pictures"   element={<ProtectedRoute><Pictures /></ProtectedRoute>} />
            <Route path="/x-analytics" element={<Navigate to="/crm/x?tab=analytics" replace />} />
            {/* OnlyFans Hubs */}
            <Route path="/onlyfans-free" element={<ProtectedRoute><OnlyFansFreeHub /></ProtectedRoute>} />
            <Route path="/onlyfans-vip" element={<ProtectedRoute><OnlyFansVIPHub /></ProtectedRoute>} />
            {/* Platform Hubs aliases */}
            <Route path="/platform-hubs/onlyfans-free" element={<ProtectedRoute><OnlyFansFreeHub /></ProtectedRoute>} />
            <Route path="/platform-hubs/onlyfans-vip" element={<ProtectedRoute><OnlyFansVIPHub /></ProtectedRoute>} />
            <Route path="/platform-hubs/jff" element={<Navigate to="/crm/jff" replace />} />
            <Route path="/platform-hubs/x" element={<Navigate to="/crm/x" replace />} />
            <Route path="/platform-hubs/faphouse" element={<ProtectedRoute><FaphouseHub /></ProtectedRoute>} />
            <Route path="/x-hub"      element={<ProtectedRoute><XStudio /></ProtectedRoute>} />
            <Route path="/x"          element={<ProtectedRoute><XStudio /></ProtectedRoute>} />
            <Route path="/shows"      element={<Navigate to="/crm/events-shows" replace />} />
            <Route path="/analytics"  element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
            <Route path="/submissions" element={<ProtectedRoute><Submissions /></ProtectedRoute>} />
            <Route path="/studios"     element={<ProtectedRoute><Studios /></ProtectedRoute>} />
            <Route path="/faphouse"        element={<ProtectedRoute><Faphouse /></ProtectedRoute>} />
            <Route path="/faphouse-hub"    element={<ProtectedRoute><FaphouseHub /></ProtectedRoute>} />
            <Route path="/consents"        element={<ProtectedRoute><Consents /></ProtectedRoute>} />
            <Route path="/backups"     element={<ProtectedRoute><Backups /></ProtectedRoute>} />
            <Route path="/crm/event-discovery" element={<ProtectedRoute><EventVenueDiscovery /></ProtectedRoute>} />
            <Route path="/crm/studio-discovery" element={<ProtectedRoute><StudioDiscovery /></ProtectedRoute>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </RouteErrorBoundary>
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-right" toastOptions={{
          style: {
            background: "#111827",
            border: "1px solid rgba(255,255,255,0.10)",
            color: "#F9FAFB",
            borderRadius: "12px",
            fontFamily: "'Manrope', sans-serif",
            fontSize: "14px",
            boxShadow: "0 16px 48px rgba(0,0,0,0.55)",
          }
        }} />
        </VoiceProvider>
      </AuthProvider>
    </div>
  );
}
